import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Users, Ticket, TrendingUp, TrendingDown, DollarSign, Activity,
  CheckCircle, XCircle, AlertTriangle, Clock, RefreshCw, Calendar,
  ArrowUpRight, ArrowDownRight, Zap, Shield, BarChart3, PieChart,
  AlertCircle, ExternalLink, Crown, Building2, Mail
} from 'lucide-react';
import { 
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { adminAPI } from '../../api';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';

// Time period options
const TIME_PERIODS = [
  { id: '1d', label: 'Last 24h' },
  { id: '7d', label: 'Last 7 Days' },
  { id: '30d', label: 'Last 30 Days' },
  { id: 'custom', label: 'Custom' }
];

// Chart colors
const COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  danger: '#ef4444',
  warning: '#f59e0b',
  purple: '#8b5cf6',
  cyan: '#06b6d4'
};

// Plan colors
const PLAN_COLORS = {
  starter: 'bg-slate-100 text-slate-700',
  growth: 'bg-blue-100 text-blue-700',
  professional: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700'
};

// KPI Card Component
const KPICard = ({ icon: Icon, label, value, change, subtext, gradient, format: formatType = 'number' }) => {
  const isPositive = change >= 0;
  const formattedValue = formatType === 'currency' 
    ? `₹${value?.toLocaleString()}` 
    : formatType === 'percent'
    ? `${value}%`
    : value?.toLocaleString();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 sm:p-2.5 rounded-xl bg-gradient-to-r ${gradient}`}>
          <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </div>
        {change !== undefined && (
          <div className={`flex items-center gap-1 text-xs sm:text-sm font-medium ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
            {isPositive ? <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" /> : <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4" />}
            {isPositive ? '+' : ''}{change}%
          </div>
        )}
      </div>
      <p className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">{formattedValue}</p>
      <p className="text-xs sm:text-sm text-slate-500">{label}</p>
      {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
    </motion.div>
  );
};

// Alert Card Component
const AlertCard = ({ alert }) => {
  const bgColors = {
    error: 'bg-red-50 border-red-200',
    warning: 'bg-amber-50 border-amber-200',
    info: 'bg-blue-50 border-blue-200'
  };
  
  const iconColors = {
    error: 'text-red-600',
    warning: 'text-amber-600',
    info: 'text-blue-600'
  };
  
  const Icon = alert.type === 'error' ? XCircle : alert.type === 'warning' ? AlertTriangle : AlertCircle;
  
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${bgColors[alert.type]}`}>
      <Icon className={`w-5 h-5 flex-shrink-0 ${iconColors[alert.type]}`} />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 text-sm">{alert.title}</p>
        <p className="text-xs text-slate-600 mt-0.5">{alert.message}</p>
      </div>
      {alert.action && (
        <Link to={alert.action} className="text-xs text-blue-600 hover:underline flex-shrink-0">
          View →
        </Link>
      )}
    </div>
  );
};

// Custom Tooltip for Charts
const CustomTooltip = ({ active, payload, label, format: formatType }) => {
  if (!active || !payload || !payload.length) return null;
  
  return (
    <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-3">
      <p className="text-xs text-slate-500 mb-2">{label}</p>
      {payload.map((entry, index) => (
        <p key={index} className="text-sm font-medium" style={{ color: entry.color }}>
          {entry.name}: {formatType === 'currency' ? `₹${entry.value?.toLocaleString()}` : entry.value?.toLocaleString()}
        </p>
      ))}
    </div>
  );
};

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('7d');
  const [customDates, setCustomDates] = useState({ start: '', end: '' });
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [data, setData] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setRefreshing(true);
      const params = { period };
      if (period === 'custom' && customDates.start && customDates.end) {
        params.start_date = customDates.start;
        params.end_date = customDates.end;
      }
      const response = await adminAPI.getDashboardAnalytics(params);
      setData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period, customDates]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handlePeriodChange = (newPeriod) => {
    if (newPeriod === 'custom') {
      setShowCustomPicker(true);
    } else {
      setShowCustomPicker(false);
      setPeriod(newPeriod);
    }
  };

  const applyCustomDates = () => {
    if (customDates.start && customDates.end) {
      setPeriod('custom');
      setShowCustomPicker(false);
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const { kpis, charts, lists, system_health, alerts } = data || {};

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Super Admin Dashboard</h1>
          <p className="text-sm text-slate-500">Business intelligence & platform overview</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchDashboardData}
            disabled={refreshing}
            className="p-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Time Period Selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-2">
          {TIME_PERIODS.map((tp) => (
            <button
              key={tp.id}
              onClick={() => handlePeriodChange(tp.id)}
              className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                period === tp.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {tp.label}
            </button>
          ))}
          
          {/* Custom Date Picker */}
          <AnimatePresence>
            {showCustomPicker && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                className="flex items-center gap-2 overflow-hidden"
              >
                <input
                  type="date"
                  value={customDates.start}
                  onChange={(e) => setCustomDates(prev => ({ ...prev, start: e.target.value }))}
                  className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm"
                />
                <span className="text-slate-400">to</span>
                <input
                  type="date"
                  value={customDates.end}
                  onChange={(e) => setCustomDates(prev => ({ ...prev, end: e.target.value }))}
                  className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm"
                />
                <button
                  onClick={applyCustomDates}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm"
                >
                  Apply
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Alerts Section */}
      {alerts && alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, index) => (
            <AlertCard key={index} alert={alert} />
          ))}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <KPICard
          icon={DollarSign}
          label="Revenue (Net)"
          value={kpis?.revenue?.value || 0}
          change={kpis?.revenue?.change}
          gradient="from-emerald-500 to-green-600"
          format="currency"
        />
        <KPICard
          icon={Users}
          label="New Partners"
          value={kpis?.partners?.new || 0}
          change={kpis?.partners?.change}
          subtext={`${kpis?.partners?.total || 0} total (${kpis?.partners?.active || 0} active)`}
          gradient="from-blue-500 to-indigo-600"
        />
        <KPICard
          icon={Activity}
          label="API Calls"
          value={kpis?.operations?.total || 0}
          change={kpis?.operations?.change}
          gradient="from-purple-500 to-pink-600"
        />
        <KPICard
          icon={CheckCircle}
          label="Success Rate"
          value={kpis?.success_rate?.value || 100}
          change={kpis?.success_rate?.change}
          gradient="from-cyan-500 to-blue-600"
          format="percent"
        />
        <KPICard
          icon={Ticket}
          label="Open Tickets"
          value={kpis?.open_tickets?.value || 0}
          subtext={kpis?.open_tickets?.pending_48h > 0 ? `${kpis?.open_tickets?.pending_48h} pending >48h` : null}
          gradient="from-amber-500 to-orange-600"
        />
      </div>

      {/* Charts Section */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Partner Growth Chart */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              Partner Growth
            </h3>
            <Link to="/admin/partners" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              View all <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          <div className="h-64">
            {charts?.partner_growth?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.partner_growth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(date) => format(parseISO(date), 'MMM d')}
                  />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="New Partners" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400">
                No data for this period
              </div>
            )}
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-600" />
              Revenue Trend
            </h3>
            <Link to="/admin/payments" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              View all <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          <div className="h-64">
            {charts?.revenue?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.revenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(date) => format(parseISO(date), 'MMM d')}
                  />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(val) => `₹${val}`} />
                  <Tooltip content={<CustomTooltip format="currency" />} />
                  <Area 
                    type="monotone" 
                    dataKey="amount" 
                    name="Revenue"
                    stroke={COLORS.success} 
                    fill={COLORS.success}
                    fillOpacity={0.2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400">
                No revenue data for this period
              </div>
            )}
          </div>
        </div>

        {/* Operations Chart */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-purple-600" />
              Operations (Success vs Failed)
            </h3>
            <Link to="/admin/operations" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              View all <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          <div className="h-64">
            {charts?.operations?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.operations}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(date) => format(parseISO(date), 'MMM d')}
                  />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="success" name="Success" stackId="a" fill={COLORS.success} />
                  <Bar dataKey="failed" name="Failed" stackId="a" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400">
                No operations data for this period
              </div>
            )}
          </div>
        </div>

        {/* API Breakdown Pie Chart */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <PieChart className="w-4 h-4 text-cyan-600" />
            API Usage Breakdown
          </h3>
          <div className="h-64 flex items-center justify-center">
            {(charts?.api_breakdown?.tryon > 0 || charts?.api_breakdown?.moments > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={[
                      { name: 'TryOn', value: charts.api_breakdown.tryon, color: COLORS.primary },
                      { name: 'Moments', value: charts.api_breakdown.moments, color: COLORS.purple }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    <Cell fill={COLORS.primary} />
                    <Cell fill={COLORS.purple} />
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <div className="text-slate-400">No API usage data for this period</div>
            )}
          </div>
        </div>
      </div>

      {/* Lists Section */}
      <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
        {/* New Partners List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              New Partners
            </h3>
            <Link to="/admin/partners" className="text-xs text-blue-600 hover:underline">
              View all →
            </Link>
          </div>
          <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {lists?.new_partners?.length > 0 ? (
              lists.new_partners.map((partner, index) => (
                <Link
                  key={partner.partner_id || index}
                  to={`/admin/partners/${partner.partner_id}`}
                  className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors"
                >
                  <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">
                      {partner.business_name || 'Unnamed'}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{partner.email}</p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${PLAN_COLORS[partner.plan] || PLAN_COLORS.starter}`}>
                      {partner.plan || 'starter'}
                    </span>
                    <p className="text-xs text-slate-400 mt-1">
                      {partner.created_at ? format(parseISO(partner.created_at), 'MMM d') : ''}
                    </p>
                  </div>
                </Link>
              ))
            ) : (
              <div className="p-6 text-center text-slate-400 text-sm">
                No new partners in this period
              </div>
            )}
          </div>
        </div>

        {/* Open Tickets List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Ticket className="w-4 h-4 text-amber-600" />
              Open Tickets
            </h3>
            <Link to="/admin/tickets" className="text-xs text-blue-600 hover:underline">
              View all →
            </Link>
          </div>
          <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {lists?.open_tickets?.length > 0 ? (
              lists.open_tickets.map((ticket, index) => (
                <Link
                  key={ticket.ticket_id || index}
                  to={`/admin/tickets`}
                  className="flex items-start gap-3 p-3 hover:bg-slate-50 transition-colors"
                >
                  <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${
                    ticket.priority === 'high' ? 'bg-red-500' :
                    ticket.priority === 'medium' ? 'bg-amber-500' : 'bg-slate-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">
                      {ticket.subject}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{ticket.partner_email}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {ticket.created_at ? format(parseISO(ticket.created_at), 'MMM d, h:mm a') : ''}
                    </p>
                  </div>
                  <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full ${
                    ticket.priority === 'high' ? 'bg-red-100 text-red-700' :
                    ticket.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {ticket.priority || 'low'}
                  </span>
                </Link>
              ))
            ) : (
              <div className="p-6 text-center text-slate-400 text-sm">
                🎉 No open tickets!
              </div>
            )}
          </div>
        </div>

        {/* Top Partners by Usage */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Zap className="w-4 h-4 text-purple-600" />
              Top Partners (by Usage)
            </h3>
            <Link to="/admin/partners" className="text-xs text-blue-600 hover:underline">
              View all →
            </Link>
          </div>
          <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {lists?.top_partners_by_usage?.length > 0 ? (
              lists.top_partners_by_usage.map((partner, index) => (
                <Link
                  key={partner.partner_id || index}
                  to={`/admin/partners/${partner.partner_id}`}
                  className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors"
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                    index === 0 ? 'bg-amber-100 text-amber-700' :
                    index === 1 ? 'bg-slate-200 text-slate-700' :
                    index === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-500'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">
                      {partner.business_name || 'Unknown'}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{partner.email}</p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-sm font-semibold text-purple-600">{partner.api_calls?.toLocaleString()}</p>
                    <p className="text-xs text-slate-400">calls</p>
                  </div>
                </Link>
              ))
            ) : (
              <div className="p-6 text-center text-slate-400 text-sm">
                No API usage in this period
              </div>
            )}
          </div>
        </div>
      </div>

      {/* System Health Panel */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
          <Shield className="w-4 h-4 text-emerald-600" />
          System Health
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className={`text-2xl font-bold ${
              system_health?.success_rate >= 97 ? 'text-emerald-600' :
              system_health?.success_rate >= 90 ? 'text-amber-600' : 'text-red-600'
            }`}>
              {system_health?.success_rate || 100}%
            </div>
            <p className="text-xs text-slate-500 mt-1">Success Rate</p>
          </div>
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className={`text-2xl font-bold ${
              system_health?.failure_rate <= 3 ? 'text-emerald-600' :
              system_health?.failure_rate <= 10 ? 'text-amber-600' : 'text-red-600'
            }`}>
              {system_health?.failure_rate || 0}%
            </div>
            <p className="text-xs text-slate-500 mt-1">Failure Rate</p>
          </div>
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className="text-2xl font-bold text-slate-900">
              {system_health?.avg_response_time_ms?.toLocaleString() || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">Avg Response (ms)</p>
          </div>
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className="text-2xl font-bold text-slate-900">
              {system_health?.total_operations_today?.toLocaleString() || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">Ops Today</p>
          </div>
        </div>
        
        {/* Status Indicator */}
        <div className="mt-4 flex items-center justify-center gap-2 p-2 bg-emerald-50 rounded-lg">
          <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          <span className="text-sm text-emerald-700 font-medium">All systems operational</span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link
          to="/admin/partners"
          className="bg-white rounded-xl border border-slate-200 p-4 hover:border-blue-300 hover:shadow-md transition-all flex items-center gap-3"
        >
          <div className="p-2 bg-blue-100 rounded-lg">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-slate-900">Manage Partners</p>
            <p className="text-xs text-slate-500">View & edit accounts</p>
          </div>
          <ArrowUpRight className="w-4 h-4 text-slate-400" />
        </Link>
        
        <Link
          to="/admin/tickets"
          className="bg-white rounded-xl border border-slate-200 p-4 hover:border-amber-300 hover:shadow-md transition-all flex items-center gap-3"
        >
          <div className="p-2 bg-amber-100 rounded-lg">
            <Ticket className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-slate-900">Support Tickets</p>
            <p className="text-xs text-slate-500">Handle requests</p>
          </div>
          <ArrowUpRight className="w-4 h-4 text-slate-400" />
        </Link>
        
        <Link
          to="/admin/payments"
          className="bg-white rounded-xl border border-slate-200 p-4 hover:border-emerald-300 hover:shadow-md transition-all flex items-center gap-3"
        >
          <div className="p-2 bg-emerald-100 rounded-lg">
            <DollarSign className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-slate-900">Payments</p>
            <p className="text-xs text-slate-500">Revenue & packs</p>
          </div>
          <ArrowUpRight className="w-4 h-4 text-slate-400" />
        </Link>
        
        <Link
          to="/admin/system-config"
          className="bg-white rounded-xl border border-slate-200 p-4 hover:border-purple-300 hover:shadow-md transition-all flex items-center gap-3"
        >
          <div className="p-2 bg-purple-100 rounded-lg">
            <Crown className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-slate-900">System Config</p>
            <p className="text-xs text-slate-500">Plans & settings</p>
          </div>
          <ArrowUpRight className="w-4 h-4 text-slate-400" />
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
